import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, LogOut, Eye, Pencil } from 'lucide-react';
import { useAuth } from '../lib/auth';

/**
 * A persistent, unmissable marker that this is somebody else's estate.
 *
 * Deliberately loud and deliberately not dismissible. The whole risk of
 * "view as customer" is an operator forgetting which estate they are in and
 * changing something in it — a banner that can be closed is a banner that will
 * be closed.
 */
export default function ImpersonationBanner() {
  const { isImpersonating, scope, tenant, org, exitImpersonation } = useAuth();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!isImpersonating) return null;
  const readOnly = scope?.impersonationMode !== 'write';

  const exit = async () => {
    setBusy(true);
    try {
      await exitImpersonation();
      navigate('/platform');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      className="sticky top-0 z-50 flex items-center gap-3 px-4 py-2 bg-purple-700 text-white text-sm"
    >
      <ShieldAlert size={16} className="shrink-0" />
      <p className="min-w-0 flex-1 truncate">
        Support session — you are inside <strong>{tenant?.name}</strong>
        {org?.name ? <> ({org.name})</> : null}, not your own estate.
      </p>
      <span className="hidden sm:flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-0.5 text-xs shrink-0">
        {readOnly ? <><Eye size={12} /> Read-only</> : <><Pencil size={12} /> Can make changes</>}
      </span>
      <button
        onClick={() => void exit()}
        disabled={busy}
        className="shrink-0 flex items-center gap-1.5 rounded-md bg-white/20 hover:bg-white/30 px-2.5 py-1 text-xs font-medium disabled:opacity-60"
      >
        <LogOut size={12} /> {busy ? 'Leaving…' : 'Leave'}
      </button>
    </div>
  );
}
